import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Funcionario } from '@/context/FuncionariosContext';
import { supabase } from '@/lib/supabase';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { temDezoitoAnosEm30Ago2023 } from '@/utils/progressaoUtils';

interface CarreiraOption {
  id: string;
  nome: string;
  nivel_maximo: number | null;
  ativo: boolean;
}

interface CarreiraNivel {
  carreira_id: string;
  nivel: number;
  vencimento_base: number | null;
}

interface FuncionarioExtra {
  email?: string;
  telefone?: string;
  data_nascimento?: string;
  cpf?: string;
  endereco?: string;
  status?: string;
  superior_hierarquico?: string;
  categoria?: string;
  dataAlteracaoCategoria?: string;
  categoriaAnterior?: string;
  despacho_autorizacao?: string;
  certificados?: string;
}

interface UnitNode {
  id: string;
  nome: string;
  filhos: UnitNode[];
}

interface FuncionarioFormModalProps {
  funcionario?: Funcionario | null;
  onSave: (data: Funcionario, oldId?: string) => void;
  onClose: () => void;
}

const FALLBACK_CARREIRAS: CarreiraOption[] = [
  { id: 'ts', nome: 'Técnica Superior', nivel_maximo: 10, ativo: true },
  { id: 'at', nome: 'Assistente Técnico', nivel_maximo: 8, ativo: true },
  { id: 'ao', nome: 'Assistente Operacional', nivel_maximo: 8, ativo: true },
];

const defaultForm: Funcionario = {
  id: '',
  nome: '',
  carreira: 'Técnica Superior',
  sexo: 'M',
  ultimaProgressao: '',
  nivelAtual: 1,
  novoNivel2025: 1,
  descricaoSubida: '',
  avaliacao: 'Adequado',
  vencimento: 0,
  unidadeOrganica: '',
  subunidadeOrganica: '',
  pontosAtuais: 0,
  pontosDescontados: 0,
  dataEntradaFuncaoPublica: '',
  excecaoPrepav: false,
};

function calcularAnosServico(dataEntrada: string): number | null {
  if (!dataEntrada) return null;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  const mesAtual = hoje.getMonth();
  const mesEntrada = entrada.getMonth();
  if (mesAtual < mesEntrada || (mesAtual === mesEntrada && hoje.getDate() < entrada.getDate())) {
    anos -= 1;
  }
  return Math.max(0, anos);
}

function buildTree(rows: { id: string; nome: string; unidade_pai_id: string | null; ordem: number }[]): UnitNode[] {
  const map: Record<string, UnitNode> = {};
  rows.forEach((r) => { map[r.id] = { id: r.id, nome: r.nome, filhos: [] }; });
  const roots: UnitNode[] = [];
  rows.forEach((r) => {
    if (r.unidade_pai_id && map[r.unidade_pai_id]) {
      map[r.unidade_pai_id].filhos.push(map[r.id]);
    } else if (!r.unidade_pai_id) {
      roots.push(map[r.id]);
    }
  });
  return roots;
}

type Section = 'identificacao' | 'contacto' | 'carreira' | 'progressao' | 'funcao' | 'remuneracao' | 'observacoes';

export default function FuncionarioFormModal({ funcionario, onSave, onClose }: FuncionarioFormModalProps) {
  const isEdit = !!funcionario;
  const { animating, handleClose } = useModalAnimation(onClose);
  const [form, setForm] = useState<Funcionario>({ ...defaultForm });
  const [extra, setExtra] = useState<FuncionarioExtra>();
  const [originalId, setOriginalId] = useState<string>('');
  const [errors, setErrors] = useState<Partial<Record<keyof Funcionario, string>>>();
  const [unidadesTree, setUnidadesTree] = useState<UnitNode[]>([]);
  const [loadingUnidades, setLoadingUnidades] = useState(true);
  const [activeSection, setActiveSection] = useState<Section>('identificacao');
  const [carreirasDisponiveis, setCarreirasDisponiveis] = useState<CarreiraOption[]>(FALLBACK_CARREIRAS);
  const [loadingCarreiras, setLoadingCarreiras] = useState(true);
  const [carreiraNiveis, setCarreiraNiveis] = useState<CarreiraNivel[]>([]);
  const [vencimentoSugerido, setVencimentoSugerido] = useState<number | null>(null);
  const [vencimentoManual, setVencimentoManual] = useState(false);
  const [validacaoProgressao, setValidacaoProgressao] = useState<{ avisos: string[]; erros: string[] } | null>(null);
  const [validandoProgressao, setValidandoProgressao] = useState(false);
  const [categoriaOriginal, setCategoriaOriginal] = useState<string>('');

  // Carregar carreiras + níveis da BD
  const loadCarreiras = useCallback(async () => {
    setLoadingCarreiras(true);
    try {
      const [{ data: cData }, { data: nData }] = await Promise.all([
        supabase.from('carreiras').select('id, nome, nivel_maximo, ativo').eq('ativo', true).order('ordem'),
        supabase.from('carreira_niveis').select('carreira_id, nivel, vencimento_base').order('nivel'),
      ]);
      if (cData && cData.length > 0) {
        setCarreirasDisponiveis(cData as CarreiraOption[]);
      } else {
        setCarreirasDisponiveis(FALLBACK_CARREIRAS);
      }
      if (nData) setCarreiraNiveis(nData as CarreiraNivel[]);
    } catch {
      setCarreirasDisponiveis(FALLBACK_CARREIRAS);
    } finally {
      setLoadingCarreiras(false);
    }
  }, []);

  useEffect(() => { loadCarreiras(); }, [loadCarreiras]);

  useEffect(() => {
    async function loadUnidades() {
      setLoadingUnidades(true);
      const { data } = await supabase.from('unidades_organicas').select('id, nome, unidade_pai_id, ordem').order('ordem');
      if (data) {
        setUnidadesTree(buildTree(data as { id: string; nome: string; unidade_pai_id: string | null; ordem: number }[]));
      }
      setLoadingUnidades(false);
    }
    loadUnidades();
  }, []);

  useEffect(() => {
    if (funcionario) {
      setForm({ ...funcionario });
      setOriginalId(funcionario.id);
      supabase
        .from('funcionarios')
        .select('email, telefone, data_nascimento, cpf, endereco, status, superior_hierarquico, categoria, data_alteracao_categoria, categoria_anterior, despacho_autorizacao, certificados')
        .eq('id', funcionario.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            const d = data as FuncionarioExtra;
            setExtra(d);
            setCategoriaOriginal(d.categoria ?? '');
          }
        });
    } else {
      setForm({ ...defaultForm });
      setExtra({});
      setOriginalId('');
    }
  }, [funcionario]);

  useModalOverflow();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  function validate(): boolean {
    const newErrors: Partial<Record<keyof Funcionario, string>> = {};
    if (!form.id.trim()) newErrors.id = 'Nº obrigatório';
    if (!form.nome.trim()) newErrors.nome = 'Nome obrigatório';
    if (form.vencimento < 0) newErrors.vencimento = 'Vencimento inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;

    // Verificar se o ID já existe (novos funcionários)
    if (!isEdit && form.id.trim()) {
      const { data: existente } = await supabase
        .from('funcionarios')
        .select('id')
        .eq('id', form.id.trim())
        .maybeSingle();
      if (existente) {
        setErrors((prev) => ({ ...prev, id: 'Já existe um funcionário com este nº' }));
        setActiveSection('identificacao');
        return;
      }
    }

    // Validar progressão via edge function (se mudar de nível)
    if (isEdit && form.novoNivel2025 !== funcionario?.novoNivel2025) {
      setValidandoProgressao(true);
      try {
        const { data: validacao } = await supabase.functions.invoke('validar-progressao', {
          body: {
            funcionario_id: originalId,
            novo_nivel: form.novoNivel2025,
            carreira_nome: form.carreira,
          },
        });
        if (validacao && !validacao.valido && validacao.erros?.length > 0) {
          setValidacaoProgressao({ avisos: validacao.avisos ?? [], erros: validacao.erros });
          setValidandoProgressao(false);
          setActiveSection('progressao');
          return;
        }
        if (validacao?.avisos?.length > 0) {
          setValidacaoProgressao({ avisos: validacao.avisos, erros: [] });
        }
      } catch {
        // Edge function falhou — continuar sem bloquear
      } finally {
        setValidandoProgressao(false);
      }
    }

    const extraFields: Record<string, string | null> = {
      email: extra.email || null,
      telefone: extra.telefone || null,
      data_nascimento: extra.data_nascimento || null,
      cpf: extra.cpf || null,
      endereco: extra.endereco || null,
      status: extra.status || null,
      superior_hierarquico: extra.superior_hierarquico || null,
      categoria: extra.categoria || null,
      data_alteracao_categoria: extra.dataAlteracaoCategoria || null,
      categoria_anterior: extra.categoriaAnterior || null,
      despacho_autorizacao: extra.despacho_autorizacao || null,
      certificados: extra.certificados || null,
    };

    // Se a categoria mudou e há uma data de alteração, registar no histórico
    if (isEdit && originalId && extra.categoria && extra.categoria !== categoriaOriginal && extra.dataAlteracaoCategoria) {
      await supabase.from('historico_categorias').insert({
        funcionario_id: originalId,
        categoria: extra.categoria,
        data_inicio: extra.dataAlteracaoCategoria,
      });
    }

    if (isEdit && originalId) {
      await supabase.from('funcionarios').update(extraFields).eq('id', originalId);
    }
    onSave(form, isEdit ? originalId : undefined);
    if (!isEdit) {
      setTimeout(async () => {
        await supabase.from('funcionarios').update(extraFields).eq('id', form.id);
      }, 500);
    }
  }

  function set<K extends keyof Funcionario>(key: K, value: Funcionario[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function setExtraField<K extends keyof FuncionarioExtra>(key: K, value: FuncionarioExtra[K]) {
    setExtra((prev) => ({ ...prev, [key]: value }));
  }

  function handleUnidadeChange(nome: string) {
    setForm((prev) => ({ ...prev, unidadeOrganica: nome, subunidadeOrganica: '' }));
  }

  function handleSubunidadeChange(nome: string) {
    setForm((prev) => ({ ...prev, subunidadeOrganica: nome }));
  }

  function findNodeByNome(nodes: UnitNode[], nome: string): UnitNode | null {
    for (const n of nodes) {
      if (n.nome === nome) return n;
      const found = findNodeByNome(n.filhos, nome);
      if (found) return found;
    }
    return null;
  }

  const unidadeNode = findNodeByNome(unidadesTree, form.unidadeOrganica);
  const subunidades = unidadeNode?.filhos ?? [];
  const subunidadeNode = findNodeByNome(subunidades, form.subunidadeOrganica);
  const subsubunidades = subunidadeNode?.filhos ?? [];

  // Carreira atual e nível máximo
  const carreiraAtual = carreirasDisponiveis.find((c) => c.nome === form.carreira);
  const nivelMax = carreiraAtual?.nivel_maximo ?? null;
  const nivelAtualExcede = nivelMax != null && form.nivelAtual > nivelMax;
  const novoNivelExcede = nivelMax != null && form.novoNivel2025 > nivelMax;

  // Calcular vencimento sugerido quando carreira ou nível mudam
  useEffect(() => {
    if (!carreiraAtual || carreiraNiveis.length === 0) {
      setVencimentoSugerido(null);
      return;
    }
    const nivelData = carreiraNiveis.find(
      (n) => n.carreira_id === carreiraAtual.id && n.nivel === form.nivelAtual
    );
    const sugestao = nivelData?.vencimento_base ?? null;
    setVencimentoSugerido(sugestao);
    // Só auto-preencher se o vencimento ainda não foi editado manualmente
    if (sugestao != null && !vencimentoManual && form.vencimento === 0) {
      setForm((prev) => ({ ...prev, vencimento: sugestao }));
    }
  }, [form.carreira, form.nivelAtual, carreiraAtual, carreiraNiveis, vencimentoManual, form.vencimento]);

  const sections: { key: Section; label: string; icon: string }[] = [
    { key: 'identificacao', label: 'Identificação', icon: 'ri-user-line' },
    { key: 'contacto', label: 'Contacto', icon: 'ri-phone-line' },
    { key: 'carreira', label: 'Carreira', icon: 'ri-briefcase-line' },
    { key: 'progressao', label: 'Progressão', icon: 'ri-arrow-up-circle-line' },
    { key: 'funcao', label: 'Função Pública', icon: 'ri-government-line' },
    { key: 'remuneracao', label: 'Remuneração', icon: 'ri-money-euro-circle-line' },
    { key: 'observacoes', label: 'Observações', icon: 'ri-file-text-line' },
  ];

  const initials = form.nome
    ? form.nome.split(' ').filter((_, idx, arr) => idx === 0 || idx === arr.length - 1).map((n) => n[0]).join('').toUpperCase()
    : '?';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl max-h-[96vh] flex flex-col overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 text-white text-sm font-bold flex-shrink-0">
              {initials}
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">
                {isEdit ? 'Editar Registo' : 'Novo Registo'}
              </p>
              <h2 className="text-[16px] font-bold text-zinc-900 leading-tight">
                {isEdit ? (form.nome || 'Funcionário') : 'Adicionar Funcionário'}
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Section nav */}
        <div className="flex items-center gap-0.5 px-5 py-2.5 border-b border-zinc-100 bg-white overflow-x-auto flex-shrink-0">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all cursor-pointer ${
                activeSection === s.key
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50'
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${s.icon} text-xs`} />
              </div>
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-6">

          {/* ── IDENTIFICAÇÃO ── */}
          {activeSection === 'identificacao' && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Nº Funcionário *
                  </label>
                  <input
                    type="text"
                    value={form.id}
                    onChange={(e) => set('id', e.target.value)}
                    placeholder="Ex: 001234"
                    className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${errors?.id ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                  />
                  {errors?.id && <p className="text-[11px] text-red-500 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line" />{errors.id}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) => set('nome', e.target.value)}
                    placeholder="Ex: Maria João Silva"
                    className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${errors?.nome ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                  />
                  {errors?.nome && <p className="text-[11px] text-red-500 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line" />{errors.nome}</p>}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Género
                </label>
                <div className="flex gap-2">
                  {(['M', 'F'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => set('sexo', s)}
                      className={`whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] rounded-lg border-2 transition-all cursor-pointer ${
                        form.sexo === s
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${s === 'M' ? 'ri-men-line' : 'ri-women-line'} text-sm`} />
                      </div>
                      {s === 'M' ? 'Masculino' : 'Feminino'}
                    </button>
                  ))}
                </div>
              </div>

              {form.nome && (
                <div className="mt-2 p-4 bg-zinc-50 rounded-xl border border-zinc-100 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-zinc-900 text-white text-sm font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-zinc-800">{form.nome}</p>
                    <p className="text-[12px] text-zinc-400 font-mono">#{form.id || '—'} · {form.sexo === 'M' ? 'Masculino' : 'Feminino'}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── CONTACTO ── */}
          {activeSection === 'contacto' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Email</label>
                  <input type="email" value={extra.email ?? ''} onChange={(e) => setExtraField('email', e.target.value)}
                    placeholder="email@exemplo.pt"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Telefone</label>
                  <input type="tel" value={extra.telefone ?? ''} onChange={(e) => setExtraField('telefone', e.target.value)}
                    placeholder="9XX XXX XXX"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Data de Nascimento</label>
                  <input type="date" value={extra.data_nascimento ?? ''} onChange={(e) => setExtraField('data_nascimento', e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">NIF / CPF</label>
                  <input type="text" value={extra.cpf ?? ''} onChange={(e) => setExtraField('cpf', e.target.value)}
                    placeholder="Ex: 123 456 789"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Morada</label>
                <input type="text" value={extra.endereco ?? ''} onChange={(e) => setExtraField('endereco', e.target.value)}
                  placeholder="Rua, nº, código postal, localidade"
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
              </div>
              {(extra.email || extra.telefone) && (
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-2">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Resumo</p>
                  {extra.email && <div className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-mail-line text-xs" /></div><span className="text-[13px] text-zinc-600">{extra.email}</span></div>}
                  {extra.telefone && <div className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-phone-line text-xs" /></div><span className="text-[13px] text-zinc-600">{extra.telefone}</span></div>}
                  {extra.endereco && <div className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-map-pin-line text-xs" /></div><span className="text-[13px] text-zinc-600">{extra.endereco}</span></div>}
                </div>
              )}
            </div>
          )}

          {/* ── CARREIRA ── */}
          {activeSection === 'carreira' && (
            <div className="space-y-5">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Carreira *
                </label>
                {loadingCarreiras ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-zinc-200 rounded-lg text-[13px] text-zinc-400">
                    <i className="ri-loader-4-line animate-spin text-sm" />
                    A carregar carreiras...
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {carreirasDisponiveis.map((c) => {
                      const isSelected = form.carreira === c.nome;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            set('carreira', c.nome);
                            if (c.nivel_maximo != null && form.nivelAtual > c.nivel_maximo) {
                              set('nivelAtual', c.nivel_maximo);
                            }
                          }}
                          className={`whitespace-nowrap flex flex-col items-start px-4 py-2.5 text-[13px] rounded-lg border-2 transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-zinc-900 text-white border-zinc-900'
                              : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                          }`}
                        >
                          <span>{c.nome}</span>
                          {c.nivel_maximo != null && (
                            <span className={`text-[10px] mt-0.5 ${isSelected ? 'text-zinc-400' : 'text-zinc-400'}`}>
                              até nível {c.nivel_maximo}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Unidade Orgânica
                </label>
                {loadingUnidades ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-zinc-200 rounded-lg text-[13px] text-zinc-400">
                    <i className="ri-loader-4-line animate-spin text-sm" />
                    A carregar...
                  </div>
                ) : (
                  <select
                    value={form.unidadeOrganica}
                    onChange={(e) => handleUnidadeChange(e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                  >
                    <option value="">— Selecionar unidade —</option>
                    {form.unidadeOrganica && !unidadesTree.find((u) => u.nome === form.unidadeOrganica) && (
                      <option value={form.unidadeOrganica}>{form.unidadeOrganica}</option>
                    )}
                    {unidadesTree.map((u) => <option key={u.id} value={u.nome}>{u.nome}</option>)}
                  </select>
                )}
              </div>

              {(subunidades.length > 0 || form.subunidadeOrganica) && (
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Subunidade Orgânica
                  </label>
                  <select
                    value={form.subunidadeOrganica}
                    onChange={(e) => handleSubunidadeChange(e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                  >
                    <option value="">— Sem subunidade —</option>
                    {form.subunidadeOrganica && !subunidades.find((s) => s.nome === form.subunidadeOrganica) && (
                      <option value={form.subunidadeOrganica}>{form.subunidadeOrganica}</option>
                    )}
                    {subunidades.map((s) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              {subsubunidades.length > 0 && (
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Sub-subunidade Orgânica
                  </label>
                  <select
                    value={''}
                    onChange={(e) => set('subunidadeOrganica', e.target.value || form.subunidadeOrganica)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                  >
                    <option value="">— Sem sub-subunidade —</option>
                    {subsubunidades.map((s) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              {(form.carreira || form.unidadeOrganica) && (
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-1.5">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Resumo</p>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-briefcase-line text-xs" /></div>
                    <span className="text-[13px] font-medium text-zinc-700">{form.carreira || '—'}</span>
                    {nivelMax != null && <span className="text-[11px] text-zinc-400">· máx. nível {nivelMax}</span>}
                  </div>
                  {form.unidadeOrganica && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-building-line text-xs" /></div>
                      <span className="text-[13px] text-zinc-600">{form.unidadeOrganica}{form.subunidadeOrganica ? ` › ${form.subunidadeOrganica}` : ''}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── PROGRESSÃO ── */}
          {activeSection === 'progressao' && (
            <div className="space-y-5">
              {nivelMax != null && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-amber-500 text-sm" />
                  </div>
                  <p className="text-[12px] text-amber-700">
                    Carreira <strong>{form.carreira}</strong> — nível máximo: <strong>{nivelMax}</strong>
                  </p>
                </div>
              )}

              {/* Erros de validação backend */}
              {validacaoProgressao?.erros && validacaoProgressao.erros.length > 0 && (
                <div className="space-y-1.5">
                  {validacaoProgressao.erros.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500 text-sm" />
                      </div>
                      <p className="text-[12px] text-red-700">{e}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Avisos de validação backend */}
              {validacaoProgressao?.avisos && validacaoProgressao.avisos.length > 0 && validacaoProgressao.erros.length === 0 && (
                <div className="space-y-1.5">
                  {validacaoProgressao.avisos.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-alert-line text-amber-500 text-sm" />
                      </div>
                      <p className="text-[12px] text-amber-700">{a}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Nível Atual
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={nivelMax ?? 99}
                    value={form.nivelAtual}
                    onChange={(e) => set('nivelAtual', parseInt(e.target.value) || 1)}
                    className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${nivelAtualExcede ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                  />
                  {nivelAtualExcede && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      Excede o nível máximo ({nivelMax}) desta carreira
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Novo Nível 2025
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={nivelMax ?? 99}
                    value={form.novoNivel2025}
                    onChange={(e) => {
                      set('novoNivel2025', parseInt(e.target.value) || 1);
                      setValidacaoProgressao(null);
                    }}
                    className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${novoNivelExcede || (validacaoProgressao?.erros?.length ?? 0) > 0 ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                  />
                  {novoNivelExcede && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      Excede o nível máximo ({nivelMax}) desta carreira
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Avaliação SIADAP
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['Excelente', 'Muito Bom', 'Adequado', 'Inadequado'] as const).map((a) => {
                    const colors: Record<string, string> = {
                      Excelente: 'bg-emerald-600 text-white border-emerald-600',
                      'Muito Bom': 'bg-amber-500 text-white border-amber-500',
                      Adequado: 'bg-zinc-900 text-white border-zinc-900',
                      Inadequado: 'bg-red-500 text-white border-red-500',
                    };
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => set('avaliacao', a)}
                        className={`whitespace-nowrap px-4 py-2.5 text-[13px] rounded-lg border-2 transition-all cursor-pointer ${
                          form.avaliacao === a ? colors[a] : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                        }`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Última Progressão
                </label>
                <input
                  type="date"
                  value={form.ultimaProgressao ?? ''}
                  onChange={(e) => set('ultimaProgressao', e.target.value)}
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                />
                {form.ultimaProgressao && (
                  <p className="text-[12px] text-zinc-400 mt-1.5">
                    {new Date(form.ultimaProgressao).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Pontos Manuais Atuais
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.pontosAtuais}
                  onChange={(e) => set('pontosAtuais', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors"
                />
                <p className="text-[11px] text-zinc-400 mt-1.5">Pontos inseridos manualmente (fora do SIADAP)</p>
              </div>

              <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-2">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Resumo</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg border border-zinc-100 p-3 text-center">
                    <p className="text-[11px] text-zinc-400 mb-1">Nível Atual</p>
                    <p className={`text-[18px] font-bold ${nivelAtualExcede ? 'text-red-500' : 'text-zinc-800'}`}>{form.nivelAtual}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-zinc-100 p-3 text-center">
                    <p className="text-[11px] text-zinc-400 mb-1">Novo Nível 2025</p>
                    <p className={`text-[18px] font-bold ${novoNivelExcede ? 'text-red-500' : 'text-zinc-800'}`}>{form.novoNivel2025}</p>
                  </div>
                  <div className="bg-white rounded-lg border border-zinc-100 p-3 text-center">
                    <p className="text-[11px] text-zinc-400 mb-1">Pontos Manuais</p>
                    <p className="text-[18px] font-bold text-zinc-800">{form.pontosAtuais}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[12px] text-zinc-500">Avaliação:</span>
                  <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${
                    form.avaliacao === 'Excelente' ? 'bg-emerald-100 text-emerald-700' :
                    form.avaliacao === 'Muito Bom' ? 'bg-amber-100 text-amber-700' :
                    form.avaliacao === 'Inadequado' ? 'bg-red-100 text-red-700' :
                    'bg-zinc-100 text-zinc-600'
                  }`}>{form.avaliacao || '—'}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── FUNÇÃO PÚBLICA ── */}
          {activeSection === 'funcao' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Categoria</label>
                  <input type="text" value={extra.categoria ?? ''} onChange={(e) => {
                    const nova = e.target.value;
                    setExtraField('categoria', nova);
                    // Se a categoria mudou e ainda não tem data de alteração, sugerir hoje
                    if (isEdit && nova !== categoriaOriginal && !extra.dataAlteracaoCategoria) {
                      setExtraField('dataAlteracaoCategoria', new Date().toISOString().split('T')[0]);
                    }
                  }}
                    placeholder="Ex: Técnico Superior de 1ª"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                    Data da Alteração de Categoria
                    {isEdit && extra.categoria && extra.categoria !== categoriaOriginal && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        <i className="ri-alert-line text-[10px]" /> Alteração detectada
                      </span>
                    )}
                  </label>
                  <input type="date" value={extra.dataAlteracaoCategoria ?? ''} onChange={(e) => setExtraField('dataAlteracaoCategoria', e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer" />
                  {extra.dataAlteracaoCategoria && (
                    <p className="text-[11px] text-zinc-400 mt-1.5">
                      Categoria vigente a partir de {new Date(extra.dataAlteracaoCategoria).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Estado</label>
                  <select value={extra.status ?? ''} onChange={(e) => setExtraField('status', e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer">
                    <option value="">— Selecionar estado —</option>
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                    <option value="Licença">Licença</option>
                    <option value="Aposentado">Aposentado</option>
                    <option value="Cedência">Cedência</option>
                    <option value="Mobilidade">Mobilidade</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Superior Hierárquico</label>
                  <input type="text" value={extra.superior_hierarquico ?? ''} onChange={(e) => setExtraField('superior_hierarquico', e.target.value)}
                    placeholder="Nome do superior hierárquico"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Despacho de Autorização</label>
                <input type="text" value={extra.despacho_autorizacao ?? ''} onChange={(e) => setExtraField('despacho_autorizacao', e.target.value)}
                  placeholder="Ex: Despacho nº 123/2024"
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors" />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Certificados / Habilitações</label>
                <textarea value={extra.certificados ?? ''} onChange={(e) => setExtraField('certificados', e.target.value)}
                  rows={3} maxLength={500} placeholder="Ex: Licenciatura em Gestão, Certificado de Formação..."
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none" />
                <div className="flex justify-end mt-1">
                  <span className={`text-[11px] font-medium ${(extra.certificados?.length ?? 0) > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>
                    {extra.certificados?.length ?? 0}/500
                  </span>
                </div>
              </div>

              <div className="border-t border-zinc-100 pt-4">
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Data de Entrada na Função Pública
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={form.dataEntradaFuncaoPublica ?? ''}
                    onChange={(e) => set('dataEntradaFuncaoPublica', e.target.value)}
                    className="flex-1 px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
                  />
                  {form.dataEntradaFuncaoPublica && (() => {
                    const anos = calcularAnosServico(form.dataEntradaFuncaoPublica);
                    return anos !== null ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-lg flex-shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-sm" /></div>
                        <span className="text-[13px] font-bold">{anos}</span>
                        <span className="text-[11px] text-zinc-400">anos</span>
                      </div>
                    ) : null;
                  })()}
                </div>

                {form.dataEntradaFuncaoPublica ? (
                  <div className="mt-3 space-y-2">
                    <p className="text-[12px] text-zinc-500">
                      Entrada em {new Date(form.dataEntradaFuncaoPublica).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                    {(() => {
                      const anosHoje = calcularAnosServico(form.dataEntradaFuncaoPublica);
                      const temRegra = temDezoitoAnosEm30Ago2023(form.dataEntradaFuncaoPublica);
                      const dataRef = new Date(2023, 7, 30);
                      const entrada = new Date(form.dataEntradaFuncaoPublica);
                      let anosNaDataRef = dataRef.getFullYear() - entrada.getFullYear();
                      if (dataRef.getMonth() < entrada.getMonth() || (dataRef.getMonth() === entrada.getMonth() && dataRef.getDate() < entrada.getDate())) anosNaDataRef -= 1;
                      anosNaDataRef = Math.max(0, anosNaDataRef);
                      if (temRegra) {
                        return (
                          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-award-line text-amber-600 text-sm" /></div>
                            <p className="text-[12px] text-amber-700 font-medium">Regra especial 2024 aplicável — {anosNaDataRef} anos de serviço a 30/08/2023 (elegibilidade com 6 pts)</p>
                          </div>
                        );
                      }
                      if (anosHoje !== null && anosHoje >= 15) {
                        return (
                          <div className="flex items-center gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-time-line text-zinc-400 text-sm" /></div>
                            <p className="text-[12px] text-zinc-500">{anosHoje} anos de serviço atuais · Regra especial 2024 não aplicável ({anosNaDataRef} anos a 30/08/2023, mínimo 18)</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-error-warning-line text-amber-500 text-sm" /></div>
                    <p className="text-[12px] text-amber-700">Campo não preenchido — necessário para verificar a regra especial de 18+ anos de serviço</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── REMUNERAÇÃO ── */}
          {activeSection === 'remuneracao' && (
            <div className="space-y-5">
              {/* Banner de sugestão automática */}
              {vencimentoSugerido != null && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                      <i className="ri-magic-line text-emerald-600 text-sm" />
                    </div>
                    <div>
                      <p className="text-[12px] font-semibold text-emerald-800">
                        Vencimento base sugerido para {form.carreira} — Nível {form.nivelAtual}
                      </p>
                      <p className="text-[13px] font-bold text-emerald-700 mt-0.5">
                        {vencimentoSugerido.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                      </p>
                    </div>
                  </div>
                  {form.vencimento !== vencimentoSugerido && (
                    <button
                      type="button"
                      onClick={() => {
                        set('vencimento', vencimentoSugerido);
                        setVencimentoManual(false);
                      }}
                      className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-check-line text-xs" />
                      Usar sugestão
                    </button>
                  )}
                  {form.vencimento === vencimentoSugerido && (
                    <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xs" />
                      Aplicado
                    </span>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Vencimento Atual (€) *
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 font-semibold">€</span>
                  <input
                    type="number" min={0} step={0.01} value={form.vencimento}
                    onChange={(e) => {
                      set('vencimento', parseFloat(e.target.value) || 0);
                      setVencimentoManual(true);
                    }}
                    className={`w-full pl-8 pr-4 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${errors?.vencimento ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}
                  />
                </div>
                {errors?.vencimento && <p className="text-[11px] text-red-500 mt-1.5">{errors.vencimento}</p>}
                {form.vencimento > 0 && (
                  <p className="text-[12px] text-zinc-400 mt-2">{form.vencimento.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })} / mês</p>
                )}
              </div>
              <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Estimativa Anual</p>
                <div className="grid grid-cols-3 gap-3">
                  {[{ label: '12 meses', value: form.vencimento * 12 }, { label: '13 meses', value: form.vencimento * 13 }, { label: '14 meses', value: form.vencimento * 14 }].map((e) => (
                    <div key={e.label} className="bg-white rounded-lg border border-zinc-100 p-3 text-center">
                      <p className="text-[11px] text-zinc-400 mb-1">{e.label}</p>
                      <p className="text-[13px] font-bold text-zinc-800">{e.value.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── OBSERVAÇÕES ── */}
          {activeSection === 'observacoes' && (
            <div className="space-y-5">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Descrição da Progressão
                </label>
                <textarea
                  value={form.descricaoSubida}
                  onChange={(e) => set('descricaoSubida', e.target.value)}
                  rows={4} maxLength={500}
                  placeholder="Descreva os motivos da progressão..."
                  className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px] text-zinc-400">Máximo 500 caracteres</span>
                  <span className={`text-[11px] font-medium ${form.descricaoSubida.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>
                    {form.descricaoSubida.length}/500
                  </span>
                </div>
              </div>

              <div className={`rounded-xl border-2 p-5 transition-all ${form.excecaoPrepav ? 'bg-amber-50 border-amber-200' : 'bg-zinc-50 border-zinc-100'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 ${form.excecaoPrepav ? 'bg-amber-100 text-amber-600' : 'bg-zinc-200 text-zinc-500'}`}>
                        <i className="ri-star-line text-sm" />
                      </div>
                      <span className={`text-[13px] font-semibold ${form.excecaoPrepav ? 'text-amber-800' : 'text-zinc-700'}`}>Exceção PREPAV</span>
                      {form.excecaoPrepav && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          <i className="ri-check-line text-xs" />Ativa
                        </span>
                      )}
                    </div>
                    <p className={`text-[12px] leading-relaxed ${form.excecaoPrepav ? 'text-amber-700' : 'text-zinc-500'}`}>
                      Quando ativa, os pontos manuais são <strong>sempre somados</strong> aos pontos SIADAP, mesmo que existam avaliações registadas. Aplica-se a funcionários abrangidos pelo regime PREPAV.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => set('excecaoPrepav', !form.excecaoPrepav)}
                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors cursor-pointer focus:outline-none ${form.excecaoPrepav ? 'bg-amber-500' : 'bg-zinc-300'}`}
                    aria-label="Ativar exceção PREPAV"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.excecaoPrepav ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/40">
          <div className="flex items-center gap-2">
            {sections.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${activeSection === s.key ? 'bg-zinc-900 w-4' : 'bg-zinc-300 hover:bg-zinc-400'}`}
                title={s.label}
              />
            ))}
          </div>
          <div className="flex items-center gap-2.5">
            <button type="button" onClick={handleClose}
              className="whitespace-nowrap px-4 py-2 text-[13px] text-zinc-600 hover:text-zinc-800 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer">
              Cancelar
            </button>
            <button type="button" onClick={handleSubmit} disabled={validandoProgressao}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-60">
              <div className="w-4 h-4 flex items-center justify-center">
                {validandoProgressao
                  ? <i className="ri-loader-4-line text-sm animate-spin" />
                  : <i className={`${isEdit ? 'ri-save-line' : 'ri-user-add-line'} text-sm`} />}
              </div>
              {validandoProgressao ? 'A validar...' : isEdit ? 'Guardar Alterações' : 'Adicionar Funcionário'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}